import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { UserCircle, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import api from '../../api';
import { useStructure } from '../../contexts/StructureContext';

const PeopleManagement = () => {
  const { currentStructure, structures } = useStructure();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    structureIds: []
  });

  useEffect(() => {
    if (currentStructure) {
      fetchPeople();
    }
  }, [currentStructure]);

  const fetchPeople = async () => {
    if (!currentStructure) {
      setError('Structure non trouvée');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/people?structureId=${currentStructure.id}`);
      setPeople(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des personnes');
      console.error('Error fetching people:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentStructure) return;

    try {
      const personData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        structureIds: formData.structureIds.length > 0 ? formData.structureIds : [currentStructure.id]
      };

      if (editingPerson) {
        await api.put(`/people/${editingPerson.id}?structureId=${currentStructure.id}`, personData);
      } else {
        await api.post('/people', personData);
      }

      await fetchPeople();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (personId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette personne ? Son historique de mouvements de stock sera conservé.')) {
      return;
    }

    if (!currentStructure) return;

    try {
      await api.delete(`/people/${personId}?structureId=${currentStructure.id}`);
      await fetchPeople();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = (person) => {
    setEditingPerson(person);
    setFormData({
      firstName: person.firstName,
      lastName: person.lastName,
      structureIds: person.structures?.map(s => s.id) || []
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({ firstName: '', lastName: '', structureIds: [] });
    setEditingPerson(null);
    setShowAddForm(false);
  };

  const toggleStructure = (structureId) => {
    setFormData(prev => ({
      ...prev,
      structureIds: prev.structureIds.includes(structureId)
        ? prev.structureIds.filter(id => id !== structureId)
        : [...prev.structureIds, structureId]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="w-6 h-6" />
            Gestion des Personnes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">
                {editingPerson ? 'Modifier la personne' : 'Nouvelle personne'}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Structures <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {structures.map(structure => (
                    <label
                      key={structure.id}
                      className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-100"
                    >
                      <input
                        type="checkbox"
                        checked={formData.structureIds.includes(structure.id)}
                        onChange={() => toggleStructure(structure.id)}
                        className="w-4 h-4"
                      />
                      <span>{structure.name}</span>
                    </label>
                  ))}
                </div>
                {formData.structureIds.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    Si aucune structure n'est sélectionnée, la structure actuelle sera utilisée par défaut.
                  </p>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  <Save size={20} />
                  {editingPerson ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                >
                  <X size={20} />
                  Annuler
                </button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Initiales
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prénom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Structures
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {people.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      Aucune personne enregistrée
                    </td>
                  </tr>
                ) : (
                  people.map((person) => (
                    <tr key={person.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary-dark font-semibold">
                          {person.initials}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {person.firstName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {person.lastName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex flex-wrap gap-1">
                          {person.structures && person.structures.length > 0 ? (
                            person.structures.map(structure => (
                              <span
                                key={structure.id}
                                className="inline-block px-2 py-1 text-xs bg-primary/10 text-primary-dark rounded"
                              >
                                {structure.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400 italic">Aucune structure</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(person)}
                          className="text-primary hover:text-primary-darker mr-4"
                          title="Modifier"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(person.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter une personne"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default PeopleManagement;
